import React, { useState } from 'react';
import { Share2, Facebook, MessageSquareText, Mail, Link2, Check, Smartphone, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

// Share one event: Facebook, X, WhatsApp, text message, email, copy link and
// the phone's own share sheet. The link is the permanent /api/share/event/{id}
// page, which carries Open Graph tags so Facebook and iMessage show a card.
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}><path d="M18.9 2H22l-7.6 8.7L23 22h-6.8l-5.3-6.9L4.8 22H1.7l8.1-9.3L0 2h7l4.8 6.3L18.9 2zm-1.2 18h1.9L6.4 3.9H4.4L17.7 20z" /></svg>
);
const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}><path d="M17.5 14.4c-.3-.1-1.8-.9-2-1s-.5-.1-.7.1-.8 1-1 1.2-.4.2-.7.1a8.2 8.2 0 0 1-2.4-1.5 9 9 0 0 1-1.7-2.1c-.2-.3 0-.5.1-.6l.5-.6.3-.5c.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6a1.1 1.1 0 0 0-.8.4 3.4 3.4 0 0 0-1.1 2.5 5.9 5.9 0 0 0 1.2 3.1 13.5 13.5 0 0 0 5.2 4.6c.7.3 1.3.5 1.7.6a4.1 4.1 0 0 0 1.9.1 3.1 3.1 0 0 0 2-1.4 2.5 2.5 0 0 0 .2-1.4c-.1-.2-.3-.3-.5-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.3a8.3 8.3 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.3 8.3 0 1 1 12 20.3z" /></svg>
);

export const shareTextFor = (ev, shareUrl) => {
  const where = [ev.venue_name, ev.address].filter(Boolean).join(', ') || (ev.online_url ? 'Online' : 'Kawartha Lakes');
  const when = `${ev.date_label}, ${ev.time_label}${ev.start_time ? ' ET' : ''}`;
  return `${ev.cancelled ? 'CANCELLED: ' : ''}${ev.title}: ${when}. ${where}. ${shareUrl}`;
};

export const ShareEvent = ({ ev, size = 'sm', className = '', testId = 'event-share' }) => {
  const [copied, setCopied] = useState(false);
  if (!ev?.id) return null;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = `${origin}/api/share/event/${ev.id}`;
  const text = shareTextFor(ev, shareUrl);
  const title = `${ev.cancelled ? 'CANCELLED: ' : ''}${ev.title}`;
  const enc = encodeURIComponent;
  const canNative = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const native = async () => {
    try { await navigator.share({ title, text: text.replace(` ${shareUrl}`, ''), url: shareUrl }); } catch { /* user closed the sheet */ }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); toast.success('Link copied. Paste it anywhere.'); setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Could not copy. Long-press the link instead: ' + shareUrl); }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size={size} variant="outline" className={`h-9 border-slate-300 text-[#1a2744] font-bold btn-press bg-white ${className}`} data-testid={testId} aria-label={`Share ${ev.title}`}>
          <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 bg-white shadow-lg border-slate-200" data-testid={`${testId}-menu`}>
        <DropdownMenuLabel className="text-xs text-slate-500 font-semibold">Tell a neighbour about this event</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {canNative && (
          <DropdownMenuItem onSelect={native} className="cursor-pointer font-semibold" data-testid={`${testId}-native`}>
            <Smartphone className="h-4 w-4 mr-2 text-[#24406f]" /> Share from this phone
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`} target="_blank" rel="noreferrer" className="cursor-pointer" data-testid={`${testId}-facebook`}>
            <Facebook className="h-4 w-4 mr-2 text-[#24406f]" /> Post on Facebook
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={`sms:?&body=${enc(text)}`} className="cursor-pointer" data-testid={`${testId}-sms`}>
            <MessageSquareText className="h-4 w-4 mr-2 text-[#24406f]" /> Text message
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={`https://wa.me/?text=${enc(text)}`} target="_blank" rel="noreferrer" className="cursor-pointer" data-testid={`${testId}-whatsapp`}>
            <WhatsAppIcon className="h-4 w-4 mr-2 text-[#24406f]" /> WhatsApp
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={`https://twitter.com/intent/tweet?text=${enc(`${title}: ${ev.date_label}`)}&url=${enc(shareUrl)}`} target="_blank" rel="noreferrer" className="cursor-pointer" data-testid={`${testId}-x`}>
            <XIcon className="h-4 w-4 mr-2 text-[#24406f]" /> Post on X
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={`mailto:?subject=${enc(title)}&body=${enc(text)}`} className="cursor-pointer" data-testid={`${testId}-email`}>
            <Mail className="h-4 w-4 mr-2 text-[#24406f]" /> Email
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); copy(); }} className="cursor-pointer" data-testid={`${testId}-copy`}>
          {copied ? <Check className="h-4 w-4 mr-2 text-emerald-600" /> : <Link2 className="h-4 w-4 mr-2 text-[#24406f]" />} {copied ? 'Copied' : 'Copy link'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
